import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { StudioPage, Panel, SectionLabel, Badge, Loading, cx } from "./StudioShared";
import { Save, Download, Upload, DatabaseBackup, History, ExternalLink, CheckCircle2, XCircle, Plug } from "lucide-react";

const inp = "w-full bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none";
const CAT_LABEL = { general: "General", branding: "Branding", security: "Security", notifications: "Notifications",
  retention: "Retention", marketplace: "Marketplace", client_portal: "Client Portal", maintenance: "System Maintenance" };
const SELECTS = { primary_brand: ["atf", "webworks"], default_language: ["en", "es", "fr"] };
const humanize = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function EnterpriseSettings() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("general");
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/admin/settings"); setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (data && data.settings[cat]) setDraft({ ...data.settings[cat] }); }, [data, cat]);

  const saveCat = async () => {
    setBusy(true);
    try { const r = await api.put(`/admin/settings/${cat}`, { values: draft }); toast.success(`${CAT_LABEL[cat]} saved (v${r.data.version}).`); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const exportAll = async () => {
    const { data: d } = await api.get("/admin/settings/export");
    const blob = new Blob([JSON.stringify(d, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "aipos-settings.json"; a.click(); URL.revokeObjectURL(url);
    toast.success("Settings exported.");
  };
  const onImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const parsed = JSON.parse(await file.text()); await api.post("/admin/settings/import", { settings: parsed.settings || parsed }); toast.success("Settings imported."); load(); }
    catch { toast.error("Import failed — expected settings JSON."); } finally { e.target.value = ""; }
  };
  const backup = async () => { setBusy(true); try { const r = await api.post("/admin/settings/backup"); toast.success(`Backup ${r.data.backup_id} created.`); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const loadHistory = async () => { const r = await api.get("/admin/settings/history"); setHistory(r.data); };

  if (!data) return <StudioPage title="Enterprise Settings" testid="enterprise-settings-page"><Loading /></StudioPage>;

  const renderField = (k, v) => {
    if (typeof v === "boolean")
      return <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={draft[k] ?? v} onChange={(e) => setDraft({ ...draft, [k]: e.target.checked })} data-testid={`set-${k}`} className="accent-gold" /> {humanize(k)}</label>;
    if (typeof v === "number")
      return <div><SectionLabel className="mb-1">{humanize(k)}</SectionLabel><input type="number" value={draft[k] ?? v} onChange={(e) => setDraft({ ...draft, [k]: Number(e.target.value) })} data-testid={`set-${k}`} className={inp} /></div>;
    if (SELECTS[k])
      return <div><SectionLabel className="mb-1">{humanize(k)}</SectionLabel><select value={draft[k] ?? v} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} data-testid={`set-${k}`} className={inp}>{SELECTS[k].map((o) => <option key={o} value={o}>{o}</option>)}</select></div>;
    return <div><SectionLabel className="mb-1">{humanize(k)}</SectionLabel><input value={draft[k] ?? v} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} data-testid={`set-${k}`} className={inp} /></div>;
  };

  return (
    <StudioPage title="Enterprise Settings" testid="enterprise-settings-page"
      question="The single source of truth for platform configuration — owned config here, everything else linked to its home."
      actions={
        <div className="flex flex-wrap gap-2">
          <label data-testid="set-import-btn" className="cursor-pointer flex items-center gap-1.5 border border-white/10 text-slate-200 px-3 py-2 text-sm hover:border-gold/50 hover:text-white rounded-sm"><Upload size={15} /> Import<input type="file" accept="application/json" className="hidden" onChange={onImport} /></label>
          <button onClick={exportAll} data-testid="set-export-btn" className="flex items-center gap-1.5 border border-white/10 text-slate-200 px-3 py-2 text-sm hover:border-gold/50 hover:text-white rounded-sm"><Download size={15} /> Export</button>
          <button onClick={backup} disabled={busy} data-testid="set-backup-btn" className="flex items-center gap-1.5 border border-white/10 text-slate-200 px-3 py-2 text-sm hover:border-gold/50 hover:text-white rounded-sm"><DatabaseBackup size={15} /> Backup</button>
        </div>}>
      <div className="grid lg:grid-cols-4 gap-6">
        {/* Category sidebar */}
        <Panel className="p-3 lg:col-span-1 h-max" data-testid="set-categories">
          <div className="space-y-0.5">
            {data.editable.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`set-cat-${c}`}
                className={cx("w-full text-left px-3 py-2 text-sm rounded-sm transition-colors", cat === c ? "bg-gold text-navy" : "text-slate-300 hover:bg-white/5")}>{CAT_LABEL[c]}</button>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-white/10 space-y-0.5">
            {[["integrations", "Integrations"], ["links", "Linked Services"], ["history", "Change History"]].map(([c, l]) => (
              <button key={c} onClick={() => { setCat(c); if (c === "history") loadHistory(); }} data-testid={`set-cat-${c}`}
                className={cx("w-full text-left px-3 py-2 text-sm rounded-sm transition-colors", cat === c ? "bg-gold text-navy" : "text-slate-400 hover:bg-white/5")}>{l}</button>
            ))}
          </div>
        </Panel>

        {/* Panel */}
        <Panel className="p-6 lg:col-span-3" data-testid="set-panel">
          {data.editable.includes(cat) ? (
            <div className="space-y-4 max-w-xl" data-testid={`set-form-${cat}`}>
              <SectionLabel>{CAT_LABEL[cat]}</SectionLabel>
              <div className="space-y-4">{Object.entries(data.settings[cat]).map(([k, v]) => <div key={k}>{renderField(k, v)}</div>)}</div>
              <button onClick={saveCat} disabled={busy} data-testid="set-save" className="bg-gold text-navy px-5 py-2.5 text-sm font-semibold rounded-sm hover:bg-gold-light disabled:opacity-60 flex items-center gap-2"><Save size={15} /> Save {CAT_LABEL[cat]}</button>
            </div>
          ) : cat === "integrations" ? (
            <div className="space-y-3" data-testid="set-integrations">
              <SectionLabel>Integrations</SectionLabel>
              {data.integrations.map((i) => (
                <div key={i.name} className="flex items-center gap-3 border border-white/10 rounded-sm px-4 py-3">
                  <Plug size={16} className="text-gold" />
                  <div className="flex-1"><div className="text-white text-sm">{i.name}</div><div className="text-slate-500 text-xs">{i.where}</div></div>
                  {i.key_present ? <Badge tone="green"><CheckCircle2 size={11} className="inline mr-1" />key set</Badge> : <Badge tone="amber"><XCircle size={11} className="inline mr-1" />awaiting key</Badge>}
                </div>
              ))}
              <p className="text-xs text-slate-500 pt-2">Secrets live in environment variables and are never displayed. Add production keys at launch.</p>
            </div>
          ) : cat === "links" ? (
            <div className="space-y-3" data-testid="set-links">
              <SectionLabel>Linked Services (single source of truth)</SectionLabel>
              <div className="text-sm text-slate-400 mb-1">Routing default: <span className="text-gold">{data.routing_summary.default_policy}</span> · {data.routing_summary.capabilities_mapped} capabilities mapped · {data.department_count} departments · roles: {data.roles.join(", ")}</div>
              {data.links.map((l) => (
                <Link key={l.category} to={l.path} data-testid={`set-link-${l.path}`} className="flex items-center gap-3 border border-white/10 rounded-sm px-4 py-3 hover:border-gold/50 transition-colors group">
                  <div className="flex-1"><div className="text-white text-sm group-hover:text-gold">{l.category}</div><div className="text-slate-500 text-xs">{l.note}</div></div>
                  <span className="text-xs text-slate-500">{l.source}</span><ExternalLink size={14} className="text-slate-500 group-hover:text-gold" />
                </Link>
              ))}
            </div>
          ) : (
            <div data-testid="set-history">
              <SectionLabel className="mb-3">Change History</SectionLabel>
              {history === null ? <Loading /> : history.length === 0 ? <p className="text-slate-500 text-sm">No changes recorded yet.</p> : (
                <div className="space-y-1.5">{history.map((h, i) => (
                  <div key={i} className="flex justify-between items-center border border-white/10 rounded-sm px-3 py-2 text-sm">
                    <span className="text-slate-300">{CAT_LABEL[h.category] || h.category} <span className="text-slate-500">· by {h.editor_name}</span></span>
                    <span className="text-slate-600 text-xs">{(h.changed_at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                ))}</div>
              )}
            </div>
          )}
        </Panel>
      </div>
    </StudioPage>
  );
}
